// modals/bank/ViewBankAccount.jsx
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { 
    X, 
    Printer, 
    Download, 
    
    User, 
  
    FileText,
   
    Star,
    TrendingUp,   
} from 'react-feather';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import { bankActions } from '../../action/bankActions';

const ViewBankAccount = ({ account, onClose }) => {
    const [accountDetails, setAccountDetails] = useState(account);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('details');

    useEffect(() => {
        if (account && !account.transactions) {
            loadAccountDetails();
        } else {
            setAccountDetails(account);
            setTransactions(account.transactions || []);
        }
    }, [account]);

    const loadAccountDetails = async () => {
        try {
            setLoading(true);
            const result = await bankActions.getAccount(account.id);
            
            if (result.success) {
                const data = result.data.account || result.data;
                setAccountDetails(data);
                
                // Load recent transactions
                const transResult = await bankActions.getTransactions(account.id, { limit: 10 });
                if (transResult.success) {
                    setTransactions(transResult.data || []);
                }
            }
        } catch (error) {
            console.error('Error loading account details:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    const formatDate = (date) => {
        return date ? dayjs(date).format('DD/MM/YYYY') : 'N/A';
    };

    const formatDateTime = (date) => {
        return date ? dayjs(date).format('DD/MM/YYYY hh:mm A') : 'N/A';
    };

    const getAccountTypeLabel = (type) => {
        const types = {
            'savings': 'Savings Account',
            'current': 'Current Account',
            'cash': 'Cash Account',
            'credit_card': 'Credit Card',
            'loan': 'Loan Account'
        };
        return types[type] || type;
    };

    const getTransactionTypeBadge = (type) => {
        if (type === 'credit') {
            return <span className="badge bg-success">Credit</span>;
        } else if (type === 'debit') {
            return <span className="badge bg-danger">Debit</span>;
        } else {
            return <span className="badge bg-info">Transfer</span>;
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const handleDownload = async () => {
        try {
            Swal.fire('Info', 'Download feature coming soon', 'info');
        } catch (error) {
            console.error('Error downloading:', error);
        }
    };

    if (!accountDetails) return null;

    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered modal-xl">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            Bank Account Details - {accountDetails.account_name}
                        </h5>
                        <div className="d-flex gap-2">
                            <button 
                                type="button" 
                                className="btn btn-outline-primary btn-sm"
                                onClick={handlePrint}
                                title="Print"
                            >
                                <Printer size={16} />
                            </button>
                            <button 
                                type="button" 
                                className="btn btn-outline-success btn-sm"
                                onClick={handleDownload}
                                title="Download Statement"
                            >
                                <Download size={16} />
                            </button>
                            <button 
                                type="button" 
                                className="btn-close" 
                                onClick={onClose}
                            ></button>
                        </div>
                    </div>
                    
                    <div className="modal-body">
                        {loading ? (
                            <div className="text-center p-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3">Loading details...</p>
                            </div>
                        ) : (
                            <>
                                {/* Header Info */}
                                <div className="row mb-4">
                                    <div className="col-md-8">
                                        <div className="card bg-light">
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <p className="mb-1 text-muted">Account Holder</p>
                                                        <h5 className="fw-bold">{accountDetails.account_name}</h5>
                                                        {accountDetails.is_default === 1 && (
                                                            <span className="badge bg-primary mt-1">
                                                                <Star size={12} className="me-1" />
                                                                Default Account
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="col-md-6">
                                                        <p className="mb-1 text-muted">Bank Name</p>
                                                        <h6>{accountDetails.bank_name}</h6>
                                                    </div>
                                                </div>
                                                <div className="row mt-2">
                                                    <div className="col-md-4">
                                                        <p className="mb-1 text-muted">Account Number</p>
                                                        <h6>{accountDetails.account_number}</h6>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <p className="mb-1 text-muted">Account Type</p>
                                                        <h6>{getAccountTypeLabel(accountDetails.account_type)}</h6>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <p className="mb-1 text-muted">IFSC Code</p>
                                                        <h6>{accountDetails.ifsc_code || 'N/A'}</h6>
                                                    </div>
                                                </div>
                                                <div className="row mt-2">
                                                    <div className="col-md-4">
                                                        <p className="mb-1 text-muted">Branch</p>
                                                        <h6>{accountDetails.branch_name || 'N/A'}</h6>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <p className="mb-1 text-muted">Currency</p>
                                                        <h6>{accountDetails.currency || 'INR'}</h6>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <p className="mb-1 text-muted">Status</p>
                                                        <h6>
                                                            <span className={accountDetails.status === 'active' ? 'badge bg-success' : 'badge bg-danger'}>
                                                                {accountDetails.status?.toUpperCase()}
                                                            </span>
                                                        </h6>
                                                    </div>
                                                </div>
                                                <div className="row mt-2">
                                                    <div className="col-md-6">
                                                        <p className="mb-1 text-muted">Created At</p>
                                                        <h6>{formatDateTime(accountDetails.created_at)}</h6>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <p className="mb-1 text-muted">Last Updated</p>
                                                        <h6>{formatDateTime(accountDetails.updated_at)}</h6>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="col-md-4">
                                        <div className="card bg-primary text-white">
                                            <div className="card-body">
                                                <h6 className="text-white mb-2">Balance Summary</h6>
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span>Current Balance:</span>
                                                    <span className="fw-bold fs-5">{formatCurrency(accountDetails.current_balance)}</span>
                                                </div>
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span>Opening Balance:</span>
                                                    <span>{formatCurrency(accountDetails.opening_balance)}</span>
                                                </div>
                                                <div className="d-flex justify-content-between pt-2 border-top border-white border-opacity-25">
                                                    <span>Net Change:</span>
                                                    <span className={accountDetails.current_balance - accountDetails.opening_balance >= 0 ? 'text-white' : 'text-danger'}>
                                                        {formatCurrency(accountDetails.current_balance - accountDetails.opening_balance)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Tabs */}
                                <ul className="nav nav-tabs mb-3">
                                    <li className="nav-item">
                                        <button
                                            className={`nav-link ${activeTab === 'details' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('details')}
                                        >
                                            <User size={14} className="me-2" />
                                            Contact Details
                                        </button>
                                    </li>
                                    <li className="nav-item">
                                        <button
                                            className={`nav-link ${activeTab === 'transactions' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('transactions')}
                                        >
                                            <TrendingUp size={14} className="me-2" />
                                            Recent Transactions
                                        </button>
                                    </li>
                                    <li className="nav-item">
                                        <button
                                            className={`nav-link ${activeTab === 'description' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('description')}
                                        >
                                            <FileText size={14} className="me-2" />
                                            Description
                                        </button>
                                    </li>
                                </ul>

                                {/* Tab Content */}
                                <div className="tab-content">
                                    {/* Contact Details Tab */}
                                    {activeTab === 'details' && (
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="card">
                                                    <div className="card-body">
                                                        <h6 className="fw-bold mb-3">Contact Information</h6>
                                                        <table className="table table-sm">
                                                            <tbody>
                                                                <tr>
                                                                    <td width="40%">Contact Person:</td>
                                                                    <td className="fw-bold">{accountDetails.contact_person || 'N/A'}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td>Phone:</td>
                                                                    <td>
                                                                        {accountDetails.contact_phone ? (
                                                                            <a href={`tel:${accountDetails.contact_phone}`}>{accountDetails.contact_phone}</a>
                                                                        ) : 'N/A'}
                                                                    </td>
                                                                </tr>
                                                                <tr>
                                                                    <td>Email:</td>
                                                                    <td>
                                                                        {accountDetails.contact_email ? (
                                                                            <a href={`mailto:${accountDetails.contact_email}`}>{accountDetails.contact_email}</a>
                                                                        ) : 'N/A'}
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="card">
                                                    <div className="card-body">
                                                        <h6 className="fw-bold mb-3">Address</h6>
                                                        <p>{accountDetails.address || 'No address provided'}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Transactions Tab */}
                                    {activeTab === 'transactions' && (
                                        <div className="table-responsive">
                                            <table className="table table-hover">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Date</th>
                                                        <th>Type</th>
                                                        <th>Description</th>
                                                        <th className="text-end">Amount</th>
                                                        <th className="text-end">Balance</th>
                                                        <th>Reference</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {transactions.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="6" className="text-center py-4">
                                                                No transactions found
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        transactions.map((trans, index) => (
                                                            <tr key={trans.id || index}>
                                                                <td>{formatDate(trans.transaction_date)}</td>
                                                                <td>{getTransactionTypeBadge(trans.transaction_type)}</td>
                                                                <td>{trans.description || '-'}</td>
                                                                <td className={`text-end fw-bold ${trans.transaction_type === 'credit' ? 'text-success' : 'text-danger'}`}>
                                                                    {trans.transaction_type === 'credit' ? '+' : '-'}{formatCurrency(trans.amount)}
                                                                </td>
                                                                <td className="text-end">{formatCurrency(trans.balance)}</td>
                                                                <td>{trans.reference_no || '-'}</td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    {/* Description Tab */}
                                    {activeTab === 'description' && (
                                        <div className="card">
                                            <div className="card-body">
                                                <p className="mb-0">{accountDetails.description || 'No description provided'}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                    
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            <X size={14} className="me-2" />
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

ViewBankAccount.propTypes = {
    account: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired
};

export default ViewBankAccount;